import { Injectable } from "@angular/core";
import { ApiPromiseState, ApiPromiseStateService } from "../../api-promise-state.service";
import { ApiSessionService } from "../../api-session.service";
import { ApiStateStoreService } from "../../api-state-store.service";
import {
  ApiTelephoneNumberAddressIdentity,
  TelephoneNumberAddressIdentity,
  TelephoneNumberAddressIdentityParams,
  ApiTelephoneNumberAddressIdentityToTelephoneNumberAddressIdentity as clean
} from "./telephone-number-address-identity";
import { ApiResourceService } from "../api-resource.service";
import { ApiAction } from "../../api-actions";
import { sessionId } from "../../apiParams/session-id";
import { extractData, OnsipApiResponse } from "../../apiResponse/response-body-new";
import { getApiActionName } from "../../onsip-api-action-new";
import { arrayToRecord } from "../../util/arrayToRecord";
import { onsipApiArrayToArray } from "../../apiResponse/xml-json";
import { organizationId } from "../../apiParams/organization-id";
import { ParameterValue } from "../../util/api-action-description";

const debug = false;

@Injectable({ providedIn: "root" })
export class TelephoneNumberAddressIdentityService extends ApiResourceService<TelephoneNumberAddressIdentity> {
  constructor(
    session: ApiSessionService,
    store: ApiStateStoreService,
    promiseState: ApiPromiseStateService
  ) {
    super(session, store, promiseState, "TelephoneNumberAddressIdentity", "username");
    debug &&
      this.state.subscribe(state => console.warn("TelephoneNumberAddressIdentityService", state));
  }

  telephoneNumberAddressIdentityBrowse(
    params: Record<string, ParameterValue> = {}
  ): ApiPromiseState<TelephoneNumberAddressIdentity> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.TelephoneNumberAddressIdentityBrowse,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiAction.TelephoneNumberAddressIdentityBrowse);
  }

  telephoneNumberAddressIdentityRead(
    Address: string
  ): ApiPromiseState<TelephoneNumberAddressIdentity> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.TelephoneNumberAddressIdentityRead,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Address
      }
    });
    return this.promiseState.toPromise(ApiAction.TelephoneNumberAddressIdentityRead);
  }

  telephoneNumberAddressIdentityEdit(
    params: TelephoneNumberAddressIdentityParams
  ): ApiPromiseState<TelephoneNumberAddressIdentity> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.TelephoneNumberAddressIdentityEdit,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiAction.TelephoneNumberAddressIdentityEdit);
  }

  telephoneNumberAddressIdentityAdd(
    params: TelephoneNumberAddressIdentityParams
  ): ApiPromiseState<TelephoneNumberAddressIdentity> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.TelephoneNumberAddressIdentityAdd,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        ...params
      }
    });
    return this.promiseState.toPromise(ApiAction.TelephoneNumberAddressIdentityAdd);
  }

  telephoneNumberAddressIdentityDelete(
    Address: string
  ): ApiPromiseState<TelephoneNumberAddressIdentity> {
    this.dispatcher.next({
      parameters: {
        Action: ApiAction.TelephoneNumberAddressIdentityDelete,
        SessionId: this.store.state.pipe(sessionId()),
        OrganizationId: this.store.state.pipe(organizationId()),
        Address
      }
    });
    return this.promiseState.toPromise(ApiAction.TelephoneNumberAddressIdentityDelete);
  }

  reducer(response: OnsipApiResponse): void {
    const action = getApiActionName(response);
    let deletedTelephoneIdentityUsername = "";
    switch (action) {
      case ApiAction.TelephoneNumberAddressIdentityRead:
      case ApiAction.TelephoneNumberAddressIdentityAdd:
      case ApiAction.TelephoneNumberAddressIdentityEdit:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            [
              extractData<ApiTelephoneNumberAddressIdentity>(response, action, "AddressIdentity")
            ].map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
      case ApiAction.TelephoneNumberAddressIdentityDelete:
        if (!response.Context.Request.Parameters) break;
        // eslint-disable-next-line no-case-declarations
        const deletedTelephoneIdentityAddress = onsipApiArrayToArray(
          response.Context.Request.Parameters,
          "Parameter"
        ).find(param => param.Name === "Address")?.Value;
        deletedTelephoneIdentityUsername =
          deletedTelephoneIdentityAddress?.substring(
            0,
            deletedTelephoneIdentityAddress.indexOf("@")
          ) || "";
        deletedTelephoneIdentityUsername &&
          this.store.mergeStateUpdate(
            this.resourceName,
            { [deletedTelephoneIdentityUsername]: undefined },
            action
          );
        break;
      case ApiAction.TelephoneNumberAddressIdentityBrowse:
        this.store.mergeStateUpdate(
          this.resourceName,
          arrayToRecord(
            extractData<Array<ApiTelephoneNumberAddressIdentity>>(
              response,
              action,
              "AddressIdentity",
              "AddressIdentities"
            ).map(clean),
            this.indexKeyName
          ),
          action
        );
        break;
    }
  }
}
